import axios from "axios";
import React, { useContext, useState } from "react";
import { FaTimes, FaWallet, FaCalendarAlt, FaUser, FaCreditCard, FaFileAlt } from "react-icons/fa";
import { MdCategory, MdAttachMoney } from "react-icons/md";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { api_url } from "../../data";
import AuthContext from "../../context/AuthContext";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";

const AddIncomeModal = ({ incomeTypes, modal, setModal, getIncomeList }) => {
  const {user} = useGlobalContext();
  const {requestWithAuth} = useAuth();
  const [data, setData] = useState({});
  const [recordingIncome, setRecordingIncome] = useState(false);

  // HANDLE CHANGE
  const handleChange = (e) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };

  // RECORD INCOME
  const recordIncome = async (e) => {
    e.preventDefault();
    try {
      setRecordingIncome(true);
      const response = await requestWithAuth(
        "/finance/income/","POST",JSON.stringify(data),{
          "Content-Type":"application/json"
        }
      )
      Swal.fire({
        title: "Income Recorded",
        text: "Income has been recorded successfully",
        icon: "success",
        confirmButtonColor: "#10B981"
      }).then(() => {
        window.location.reload();
      });
      
    } catch (error) {
      if(error.response.status === 400){
        toast.error(error.response.data.message)
      }
      toast.error("An error occurred.");
    }finally{
      setRecordingIncome(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-green-500 to-green-600 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
              <FaWallet className="text-white text-lg" />
            </div>
            <h3 className="text-xl font-semibold text-white">Record New Income</h3>
          </div>
          <button
            onClick={() => setModal(!modal)}
            className="h-8 w-8 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center text-white transition-colors duration-200"
          >
            <FaTimes className="text-sm" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <form onSubmit={(e) => recordIncome(e)} className="space-y-6">
            {/* Date Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <FaCalendarAlt className="text-green-500" />
                Date Received
              </label>
              <input
                type="date"
                onChange={(e) => handleChange(e)}
                name="date_received"
                id="date_received"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Income Type Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <MdCategory className="text-green-500" />
                Income Type
              </label>
              <select
                required
                name="income_type"
                id="income_type"
                onChange={(e) => handleChange(e)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-white"
              >
                <option value="">Select Income Type</option>
                {incomeTypes?.map((incomeType) => (
                  <option value={incomeType.id} key={incomeType.id}>
                    {incomeType.name}
                  </option>
                ))}
                {user?.role !== "Welfare Manager" && (
                  <option value="Other">Other (Create New)</option>
                )}
              </select>
            </div>

            {/* New Income Type Field (Conditional) */}
            {data?.income_type === "Other" && (
              <div className="space-y-2 bg-blue-50 p-4 rounded-lg border border-blue-200">
                <label className="flex items-center gap-2 text-sm font-medium text-blue-700">
                  <MdCategory className="text-blue-500" />
                  New Income Type Name
                </label>
                <input
                  type="text"
                  name="new_income_type"
                  id="new_income_type"
                  placeholder="Enter new income type name"
                  onChange={(e) => handleChange(e)}
                  required={data?.income_type === "Other"}
                  className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
                <p className="text-xs text-blue-600">This will create a new income category for future use.</p>
              </div>
            )}

            {/* Amount Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <MdAttachMoney className="text-green-500" />
                Amount Received (GHS)
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="amount_received"
                  id="amount_received"
                  placeholder="0.00"
                  onChange={(e) => handleChange(e)}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 pl-12"
                />
                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                  GHS
                </span>
              </div>
            </div>

            {/* Payer Name Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <FaUser className="text-green-500" />
                Payer Name
              </label>
              <input
                type="text"
                name="payer_name"
                id="payer_name"
                placeholder="Enter payer's name (optional)"
                onChange={(e) => handleChange(e)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Payment Method Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <FaCreditCard className="text-green-500" />
                Payment Method
              </label>
              <select
                required
                name="payment_method"
                id="payment_method"
                onChange={(e) => handleChange(e)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-white"
              >
                <option value="">Select Payment Method</option>
                <option value="Cash">💵 Cash</option>
                <option value="Mobile Money">📱 Mobile Money</option>
                <option value="Bank Transfer">🏦 Bank Transfer</option>
                <option value="Check">📝 Check</option>
                <option value="Online Transfer">💳 Online Transfer</option>
              </select>
            </div>

            {/* Description Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <FaFileAlt className="text-green-500" />
                Description
              </label>
              <textarea
                name="description"
                id="description"
                cols="30"
                rows="4"
                required
                placeholder="Describe the source or purpose of this income..."
                onChange={(e) => handleChange(e)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 resize-none"
              ></textarea>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setModal(!modal)}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              
              {recordingIncome ? (
                <div className="flex-1 flex items-center justify-center px-6 py-3 bg-green-600 rounded-lg">
                  <ThreeDots
                    height="20"
                    width="40"
                    radius="9"
                    color="#ffffff"
                    ariaLabel="three-dots-loading"
                    visible={true}
                  />
                </div>
              ) : (
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <FaWallet className="text-sm" />
                  Record Income
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddIncomeModal;